import {ArtifactId, ExpeditionId, BrowserSessionStateId, DeviceId, ProjectId, StateDataId, ScreenshotId, ImageUrl, UserId, FactSessionId} from './ids';
import {DateTime} from '../types/base-types';
import {Artifact} from '../types/artifact';
import {Browser, browserFromBackendObject} from './browser';
import {BrowserSessionInfoDto} from '@webmate-web-core/shared/api/rest/browserSession.restapi';



export type SpanId = string;


/**
 * Represents a BrowserSession containing references to BrowserSession artifacts, etc.
 */
// export class BrowserSession {
//   constructor(public readonly id: BrowserSessionId, public readonly browser: Browser, public readonly sessionState: string, public readonly optStart: Date | null,
//               public readonly optEnd: Date | null, public readonly associatedStates: List<BrowserSessionStateId>) {
//   }
// }

/**
 * Represents a BrowserSessionInfo with all relevant information about a BrowserSession (but with no artifacts).
 */
export class BrowserSessionInfo {
    constructor(public readonly id: ExpeditionId, public readonly browser: Browser, public readonly sessionState: string, public readonly user: UserId, public readonly projectId: ProjectId,
                public readonly optStart: DateTime | null, public readonly optEnd: DateTime | null, public readonly optDeviceId?: DeviceId,
                public currentPageTitle?: string, public readonly startupFactSessionId?: FactSessionId) {}

    public isActive(): boolean {
        return this.sessionState == "ready" || this.sessionState == "running" || this.sessionState == "leased" || this.sessionState == "starting";
    }

    static fromBackendObject(obj: BrowserSessionInfoDto): BrowserSessionInfo {
        let browserFromBackend = browserFromBackendObject(obj.browser);
        return new BrowserSessionInfo(obj.id, browserFromBackend, obj.sessionState, obj.user, obj.projectId, obj.optStart, obj.optEnd, obj.optDeviceId,
            undefined, obj.startupFactSessionId ?? undefined);
    }
}

/**
 * Represents a state of a tested / visited web application (without the actual artifacts).
 */
export class StateInfo {
    constructor(public readonly id: BrowserSessionStateId, public readonly sessionId: ExpeditionId, public readonly start: Date, public readonly end: Date | null,
                public readonly description: string, public readonly associatedArtifactIds: ArtifactId[], public readonly firstScreenshotUrl: string | null,
                public readonly firstScreenshotThumbnailUrl: string | null = null, public readonly firstScreenshotId: ScreenshotId | null = null) {
    }

    /**
     * Check whether the given artifactId is contained in the list of assocated State artifacts.
     */
    isAssociatedWithArtifact(artifactId: ArtifactId): boolean {
        return !!this.associatedArtifactIds.find(item => item === artifactId);
    }

    isMockState(): boolean {
        return false;
    }
}

/**
 * Represents a state of a tested / visited web application.
 */
export class State {
  constructor(public readonly id: BrowserSessionStateId, public readonly sessionId: ExpeditionId, public readonly start: Date, public readonly end: Date | null,
              public readonly description: string, public readonly associatedArtifacts: Artifact[], public readonly firstScreenshotUrl: ImageUrl | null,
              public readonly firstScreenshotThumbnailUrl: ImageUrl | null = null, public readonly firstScreenshotId: ScreenshotId | null = null, public readonly provenance: StateProvenanceInfo | null = null) {
  }

  /**
   * Check whether the given artifactId is contained in the list of assocated State artifacts.
   */
  isAssociatedWithArtifact(artifactId: ArtifactId): boolean {
    return !!this.associatedArtifacts.find(item => item?.id === artifactId);
  }

    isMockState(): boolean {
        return false;
  }
}

export interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface FullRectangle {
    x: number;
    y: number;
    width: number;
    height: number;
    xRel: number;
    yRel: number;
    widthRel: number;
    heightRel: number;
}

export type CssElementSelector = {
    'type': 'CssSelector';
    'selector': string;
}

export type MobileElementSelector = {
    'type': 'MobileElementSelector';
    'strategy': {
        'type': 'AccessibilityId' | 'Id' | 'Text' | 'XPath';
    };
    'value': string;
}

export type ElementSelector = CssElementSelector | MobileElementSelector;

export interface ElementDescriptor {
    fullBoundingBox?: FullRectangle;
    mainSelector?: ElementSelector;
    htmlString?: string;
    elementScreenshot?: ScreenshotId;
    tagName?: string;
    properties?: Record<string, string>;
    attributes?: any;
    computedStyle?: any;
    visibleText?: string;
    xpath?: string;
}

export type InteractionStep = any;

export interface NativeInteractionPath {
    steps: InteractionStep[]
}

export interface SerializedPlaywrightPath {
    actions: any[]; // JsValue is not defined in TypeScript, so we use any[]
}


export type InteractionPath = NativeInteractionPath | SerializedPlaywrightPath;

export interface StateProvenanceInfo {
    path?: InteractionPath;
    contextInfo?: any;
}

/**
 * An Artifact that represents a State in the BrowserSession. This is the "physical" representation of a State in the browsersession Artifacts.
 * The frontend "State" concept (see class 'State' above) also encompasses other physical Artifacts types, such as a representative Screenshot artifact.
 */
export class BrowserSessionStateArtifact {
    constructor(public readonly id: ArtifactId, public readonly projectId: ProjectId, public readonly creationTime: DateTime,
                public readonly browserSession: ExpeditionId, public readonly name: string, public readonly provenance?: StateProvenanceInfo) {}

    static fromArtifact(artifact: Artifact): BrowserSessionStateArtifact {

        if (!(artifact.data['name'])) {
            throw new Error("BrowserSessionArtifact must have a 'name' data field");
        }
        if (!artifact.associations.find(a => a.associationType === "BrowserSession")) {
            throw new Error("BrowserSessionArtifact must have a browserSession association");
        }
        return new BrowserSessionStateArtifact(artifact.id, artifact.projectId, artifact.creationTime,
            artifact.associations.find(a => a.associationType === "BrowserSession")!.id, artifact.data['name'], artifact.data['provenance']);
    }
}

export class MockState extends State {
  constructor() {
    super("00000000-0000-0000-0000-000000000000", "00000000-0000-0000-0000-000000000000", new Date("1970-01-01T00:00:00.000Z"), null,
      "00000000-0000-0000-0000-000000000000", [], null, null);
  }

  isAssociatedWithArtifact(artifactId: ArtifactId): boolean {
    return false;
  }

  isMockState(): boolean {
    return true;
  }
}

export class StateDifference {
  constructor(public readonly issueType: string, public readonly associatedArtifacts: ArtifactId[], public readonly properties: any) {
  };
}

export interface StateDataInfo {
    id: StateDataId,
    title: string,
    properties: any
}

/// Policies for Automation

export type AutomationPolicyPresetSpec = {
    type: 'ClickPolicyPreset';
    selectors: string[];
} | {
    type: 'JavascriptPolicyPreset';
    script: string;
} | {
    type: 'MobileClickPolicyPreset';
    selector: Omit<MobileElementSelector, 'type'>;
} | ({
    type: string;
} & Record<string, any>);

export type PolicyScope =
    | { type: 'Session' }
    | { type: 'OncePerPage' }
    | { type: 'UntilSuccess' };

export type PolicyTriggerPoint =
  | { type: 'OnSessionStart' }
  | { type: 'OnDocumentStart' }
  | { type: 'AfterNavigation' }
  | { type: 'OnDomChange' }
  | { type: 'AfterSalientInteraction' }
  | { type: 'BeforeSalientInteraction' };

export type Selector = { type: 'css'; value: string } | { type: 'xpath'; value: string };

export type CssSelector = string;


export type InvariantSpec =
    | { type: 'AlwaysRemediate' }
    | { type: 'ElementPresent'; selector: string }
    | { type: 'AllElementsAreAbsent'; selectors: CssSelector[] }
    | { type: 'FieldValueEquals'; selector: string; value: string }
    | { type: 'JavaScriptExecuted'; markerId?: string }
    | { type: 'LocalStorageEquals'; key: string; value: string }
    | { type: 'AllCookiesMatch'; cookies: { name: string; value: string }[] };

export type CookieItem = { name: string; value: string };


export type FieldTarget =
    | { type: 'selector'; selector: string }
    | { type: 'label'; text: string }
    | { type: 'name'; name: string }
    | { type: 'placeholder'; text: string };

export type InputMode =
    | { type: 'nativeSet' }
    | { type: 'type' }
    | { type: 'typeWithDelay'; ms: number };

export type FieldSpec = {
    target: FieldTarget;
    value: string;
    clearFirst?: boolean;
    focus?: boolean;
    inputMode?: InputMode;
    maskInLogs?: boolean;
};

export type SubmitAction =
    | { type: 'none' }
    | { type: 'pressEnterOnLastFilled' }
    | { type: 'clickSelector'; selector: string }
    | { type: 'submitNearestFormOfLastFilled' };

export type RemediationSpec =
    | { type: "ClickAnyFoundElement"; selectors: CssSelector[] }
    | { type: "FillFields"; fields: FieldSpec[]; submit?: SubmitAction; scrollIntoView?: boolean }
    | { type: "SetLocalStorage"; key: string; value: string }
    | { type: "SetCookies"; cookieItems: CookieItem[] }
    | { type: "ExecuteJavaScript"; script: string }
    | { type: "ClickMobileElement"; selector: Omit<MobileElementSelector, 'type'> };

export type ConditionSpec =
    | { type: 'UrlMatches'; pattern: string }
    | { type: 'UrlNotMatches'; pattern: string }
    | { type: 'HostIs'; host: string }
    | { type: 'DomContains'; selector: string };

export type BrowserStatePredicate =
    | { type: 'Always' }
    | { type: 'Never' }
    | { type: 'UrlMatches'; pattern: string }
    | { type: 'UrlNotMatches'; pattern: string }
    | { type: 'HostIs'; host: string }
    | { type: 'DomContains'; selector: string }
    | { type: 'AllOf'; items: BrowserStatePredicate[] }
    | { type: 'AnyOf'; items: BrowserStatePredicate[] };

export interface AutomationPolicySpec {
    name: string;
    invariant: InvariantSpec;
    remediation: RemediationSpec;
    triggers: PolicyTriggerPoint[];
    scope: PolicyScope;
    conditions?: ConditionSpec[];
    applicability?: BrowserStatePredicate;
}

export type AutomationPolicyConfig = {
    presetSpecs?: AutomationPolicyPresetSpec[];
    policySpecs?: AutomationPolicySpec[];
}
